import React from 'react';
import { useIntl } from 'react-intl';
import { MdPlayArrow } from 'react-icons/md';
import { Button, PromoBanner, PROMO_BANNER_DENSITY_COMPACT, PROMO_BANNER_TONE_SUCCESS } from '@aibuild/ui';
import type { AppRunController } from './appFrame/useAppRunController';
import { usePendingAppStart, withDeclaredOptions } from './usePendingAppStart';

interface ResumeRunBannerProps {
  readonly controller: AppRunController;
}

/** The Run a visitor asked for before they had an account, one click away once they have one. */
const ResumeRunBanner: React.FC<ResumeRunBannerProps> = ({ controller }) => {
  const intl = useIntl();
  const { pending, forget } = usePendingAppStart(controller.appKey, !controller.runId && !controller.readOnly);
  const { manifest } = controller;
  if (!pending || !manifest) return null;

  const resume = () => {
    controller
      .startRun(withDeclaredOptions(pending, manifest))
      .then(forget)
      .catch(() => undefined);
  };
  const files = pending.files.map((file) => file.name).join(', ');

  return (
    <PromoBanner
      density={PROMO_BANNER_DENSITY_COMPACT}
      tone={PROMO_BANNER_TONE_SUCCESS}
      icon={<MdPlayArrow />}
      title={
        pending.example || !files
          ? intl.formatMessage({
              id: 'apps.run.resume.plain',
              defaultMessage: 'Pick up where you left off: run this app as you set it up before signing in.',
            })
          : intl.formatMessage(
              {
                id: 'apps.run.resume.files',
                defaultMessage: 'Pick up where you left off: run this app on {files}.',
              },
              { files },
            )
      }
      actions={
        <>
          <Button size="sm" onClick={resume} disabled={controller.isBusy} dataTestId="app-run__resume-continue">
            {intl.formatMessage({ id: 'apps.run.resume.continue', defaultMessage: 'Continue' })}
          </Button>
          <Button variant="text" size="sm" onClick={() => void forget()} dataTestId="app-run__resume-discard">
            {intl.formatMessage({ id: 'apps.run.resume.discard', defaultMessage: 'Discard' })}
          </Button>
        </>
      }
      dataTestId="app-run__resume"
    />
  );
};

export default ResumeRunBanner;
